/* All bundle entries share this checked contract. Never clear a cart or retry an ambiguous write. */
(() => {
  'use strict';
  const properties = item => Object.entries(item.properties || {}).sort(([a],[b]) => a.localeCompare(b));
  const plain = item => properties(item).length === 0 && !item.selling_plan_allocation;
  const signature = items => JSON.stringify(items.map(item => [item.key, Number(item.variant_id),
    item.quantity, item.original_price, item.final_price, item.final_line_price, properties(item),
    item.selling_plan_allocation || null, item.discounts || []]));
  const reviewed = (item, prices) => plain(item) && item.quantity === 1 &&
    Object.hasOwn(prices, item.variant_id) && item.original_price === prices[item.variant_id] &&
    item.final_price === prices[item.variant_id] && item.final_line_price === prices[item.variant_id];
  function checkSnapshot(cart, expected) {
    if (cart.currency !== 'AUD' || signature(cart.items) !== signature(expected) ||
        cart.total_price !== expected.reduce((sum,item)=>sum + item.final_line_price,0)) {
      throw new Error('Your cart changed. Reload it to review the saved contents.');
    }
  }
  async function checkProduct(api, targetId, prices) {
    const product = await api.product();
    const variant = product.variants.find(v => Number(v.id) === Number(targetId));
    if (product.handle !== 'reviva-mouth-tape' || product.variants.length !== 3 ||
        product.requires_selling_plan || !variant?.available || variant.requires_selling_plan ||
        !Object.hasOwn(prices,targetId) || variant.price !== prices[targetId]) {
      throw new Error('This package is unavailable or its price changed. Reload to review current availability, or contact Reviva.');
    }
  }
  // Exactly one base bundle and at most one explicitly opted-in extra pack.
  // Unknown, discounted, personalised or recurring lines stay in recovery.
  function packageCart(items, prices, bump) {
    const bases = items.filter(item => reviewed(item, prices));
    const extras = bump ? items.filter(item => reviewed(item, {[bump.id]:bump.price})) : [];
    return bases.length === 1 && extras.length <= 1 && items.length === bases.length + extras.length;
  }
  async function checkExtra(api, bump) {
    const product = await api.extraProduct();
    const variant = product.variants.find(v => Number(v.id) === Number(bump.id));
    if (product.handle !== bump.handle || product.variants.length !== 1 ||
        product.requires_selling_plan || !variant?.available || variant.requires_selling_plan ||
        variant.price !== bump.price) throw new Error('The extra pack is unavailable or its price changed. Reload to review your saved cart.');
  }
  async function setExtra(api, expected, enabled, prices, bump) {
    if (!bump || typeof enabled !== 'boolean' || !packageCart(expected, prices, bump)) {
      throw new Error('Review your package before choosing the extra pack.');
    }
    const before = await api.get();
    checkSnapshot(before, expected);
    const extra = before.items.find(item => Number(item.variant_id) === Number(bump.id));
    if (enabled) {
      await checkExtra(api, bump);
      if (extra) return before; // Restored checked state is never another addition.
      await api.add({id:Number(bump.id), quantity:1});
      const after = await api.get();
      const added = after.items.filter(item => Number(item.variant_id) === Number(bump.id));
      if (added.length !== 1 || !reviewed(added[0], {[bump.id]:bump.price})) {
        throw new Error('The extra pack needs review. Reload before continuing.');
      }
      checkSnapshot(after, after.items);
      // Shopify may reorder lines, but every pre-existing line must remain byte-for-byte unchanged.
      if (signature(after.items.filter(item => Number(item.variant_id) !== Number(bump.id))) !== signature(before.items)) {
        throw new Error('Your cart changed while adding the extra pack. Reload before continuing.');
      }
      return after;
    }
    if (!extra) return before;
    await api.change({id:extra.key, quantity:0});
    const after = await api.get();
    checkSnapshot(after, before.items.filter(item => item.key !== extra.key));
    return after;
  }
  async function switchPackage(api, expected, sourceKey, targetId, prices, bump = null) {
    await checkProduct(api, targetId, prices);
    const before = await api.get();
    checkSnapshot(before, expected);
    const source = before.items.find(item => item.key === sourceKey);
    if (!packageCart(before.items, prices, bump) || !source || !reviewed(source, prices)) {
      throw new Error('Review the saved items below. Remove incompatible items only if you no longer want them.');
    }
    if (Number(source.variant_id) === Number(targetId)) return before;
    // Add first: unavailable inventory must never remove the original bundle.
    await api.add({id:Number(targetId), quantity:1});
    const added = await api.get();
    const target = added.items.filter(item => Number(item.variant_id) === Number(targetId));
    const withoutTarget = added.items.filter(item => Number(item.variant_id) !== Number(targetId));
    if (added.currency !== 'AUD' || added.total_price !== before.total_price + prices[targetId] ||
        target.length !== 1 || !reviewed(target[0], prices) ||
        signature(withoutTarget) !== signature(before.items)) {
      throw new Error('The package change needs review. Both packages may be saved; reload before continuing.');
    }
    await api.change({id:sourceKey, quantity:0});
    const after = await api.get();
    checkSnapshot(after, added.items.filter(item => item.key !== sourceKey));
    return after;
  }
  async function selectPackage(api, expected, targetId, prices, bump = null) {
    if (expected.length) return switchPackage(api, expected, expected.find(item => reviewed(item,prices))?.key, targetId, prices, bump);
    await checkProduct(api, targetId, prices);
    const before = await api.get();
    checkSnapshot(before, expected);
    await api.add({id:Number(targetId), quantity:1});
    const after = await api.get();
    if (after.currency !== 'AUD' || after.total_price !== prices[targetId] || after.items.length !== 1 ||
        Number(after.items[0].variant_id) !== Number(targetId) || !reviewed(after.items[0], prices)) {
      throw new Error('The saved package needs review. Reload before continuing.');
    }
    return after;
  }
  async function removeReviewed(api, expected, key) {
    const before = await api.get();
    // Recovery works in any currency, but still requires exactly the reviewed snapshot.
    if (signature(before.items) !== signature(expected) || !expected.some(i => i.key === key)) {
      throw new Error('Your cart changed. Reload before removing anything.');
    }
    await api.change({id:key, quantity:0});
    const after = await api.get();
    if (after.currency !== before.currency || signature(after.items) !== signature(expected.filter(i => i.key !== key))) {
      throw new Error('Your cart changed during removal. Reload to review it.');
    }
    return after;
  }
  if (typeof module !== 'undefined' && module.exports) module.exports = {signature, switchPackage, selectPackage, removeReviewed, packageCart, setExtra};
  if (typeof document === 'undefined') return;
  const form = document.querySelector('[data-reviva-cart]');
  if (!form) return;
  const expected = JSON.parse(form.dataset.cartState);
  const radios = [...form.querySelectorAll('[name="reviva_package"]')];
  const prices = Object.fromEntries(radios.map(r => [r.value, Number(r.dataset.offerPrice)]));
  const allowed = new Map(radios.map(r => [r, !r.disabled]));
  const bump = form.dataset.extraOffer ? JSON.parse(form.dataset.extraOffer) : null;
  const extraControl = form.querySelector('[data-extra-opt-in]');
  const checkout = form.querySelector('[data-cart-checkout]');
  const add = form.querySelector('[data-package-add]');
  const removals = [...form.querySelectorAll('[data-remove-line]')];
  const confirmations = removals.map(button => {
    const panel = document.getElementById(button.getAttribute('aria-controls'));
    return {button, panel, cancel:panel.querySelector('[data-remove-cancel]'),
      confirm:panel.querySelector('[data-remove-confirm]')};
  });
  const error = form.querySelector('[data-cart-error]');
  const cartUrl = form.dataset.cartUrl;
  const selected = form.dataset.cartValid === 'true' && packageCart(expected,prices,bump) ? expected.find(item => reviewed(item,prices)) : null;
  const incoming = new URLSearchParams(location.search).get('reviva_variant');
  const empty = expected.length === 0;
  let busy = false, failed = false, pendingRemoval = null;
  const attributed = path => window.RevivaAttribution ? window.RevivaAttribution.url(path) : path;
  const savedUrl = () => attributed(cartUrl + '?view=reviva-packages');
  function refresh() {
    if (checkout) checkout.disabled = busy || failed || !!pendingRemoval || !selected;
    if (add) add.disabled = busy || failed || !!pendingRemoval || !empty;
    if (extraControl) {
      extraControl.checked = expected.some(item => Number(item.variant_id) === Number(bump.id));
      extraControl.disabled = busy || failed || !!pendingRemoval || !selected;
    }
    radios.forEach(r => {
      if (selected) r.checked = Number(r.value) === Number(selected.variant_id);
      r.disabled = !allowed.get(r) || (!selected && !empty) || busy || failed || !!pendingRemoval;
    });
    confirmations.forEach(entry => {
      const open = entry === pendingRemoval;
      entry.button.disabled = busy || failed || !!pendingRemoval;
      entry.button.setAttribute('aria-expanded', String(open));
      entry.panel.hidden = !open;
      entry.cancel.disabled = entry.confirm.disabled = busy || failed;
    });
    form.setAttribute('aria-busy', String(busy));
  }
  function fail(message) {
    failed = true; busy = false; pendingRemoval = null;
    error.replaceChildren(document.createTextNode(message+' No automatic retry was made. '));
    const link = document.createElement('a'); link.href=savedUrl(); link.textContent='Review saved cart';
    error.append(link); error.hidden=false; refresh(); link.focus();
  }
  async function request(path, body) {
    const response = await fetch(path, {credentials:'same-origin', cache:'no-store',
      ...(body ? {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(body)} : {})});
    if (!response.ok) throw new Error('Shopify could not save the change. Reload to check your saved contents.');
    return response.json();
  }
  const api = {get:()=>request(cartUrl+'.js'), add:body=>request(cartUrl+'/add.js',body),
    change:body=>request(cartUrl+'/change.js',body), product:()=>request(form.dataset.productUrl),
    update:body=>request(cartUrl+'/update.js',body), extraProduct:()=>request(form.dataset.extraProductUrl)};
  // Web Locks serialize this funnel across tabs; without them fail closed, not best-effort writes.
  async function locked(action) {
    if (!navigator.locks) throw new Error('Safe cart updates need a current browser. Try an updated browser or contact Reviva.');
    return navigator.locks.request('reviva-cart-selection', {mode:'exclusive'}, action);
  }
  async function choose(target) {
    if (busy || failed || pendingRemoval) return;
    const radio = radios.find(r => r.value === String(target));
    if (!radio || !allowed.get(radio)) {fail('The requested package is unavailable.'); return;}
    busy=true; refresh();
    try {
      const operation = {intent: !expected.length ? 'add' :
        Number(selected?.variant_id) === Number(target) ? 'noop' : 'replace', targetId:Number(target)};
      await locked(async()=>{
        const after = await selectPackage(api,expected,target,prices,bump);
        try {window.RevivaCartGoal?.recordCommitted({operation,before:expected,after:after.items,currency:after.currency});}
        catch (_) { /* A tracking failure must never block or retry commerce. */ }
      });
      location.replace(savedUrl());
    } catch(e) {fail(e.message);}
  }
  if ((selected || empty) && form.dataset.currency === 'AUD') form.querySelector('[data-package-picker]').hidden=false;
  radios.forEach(radio=>radio.addEventListener('change',()=>{if (!empty) return choose(radio.value);}));
  extraControl?.addEventListener('change', async () => {
    if (busy || failed || pendingRemoval || !selected) return;
    const optedIn = extraControl.checked;
    busy=true; refresh();
    try {
      await locked(()=>setExtra(api,expected,optedIn,prices,bump));
      location.replace(savedUrl());
    } catch(e) {fail(e.message);}
  });
  confirmations.forEach(entry => {
    entry.button.addEventListener('click', () => {
      if (busy || failed || pendingRemoval) return;
      pendingRemoval=entry; refresh(); entry.cancel.focus();
    });
    function cancel() {
      if (busy || failed || pendingRemoval !== entry) return;
      pendingRemoval=null; refresh(); entry.button.focus();
    }
    entry.cancel.addEventListener('click', cancel);
    entry.panel.addEventListener('keydown', event => {
      if (event.key === 'Escape') {event.preventDefault(); cancel();}
    });
    entry.confirm.addEventListener('click', async () => {
      if (busy || failed || pendingRemoval !== entry) return;
      busy=true; refresh();
      try {
        await locked(()=>removeReviewed(api,expected,entry.button.dataset.removeLine));
        location.replace(savedUrl());
      } catch(e) {fail(e.message);}
    });
  });
  form.addEventListener('submit',async event=>{
    event.preventDefault();
    if (busy || failed || pendingRemoval) return;
    if (event.submitter === add && add) return choose(radios.find(r=>r.checked && allowed.get(r))?.value);
    if (event.submitter !== checkout || !selected) return;
    busy=true; refresh();
    try {
      await locked(async()=>{
        await checkProduct(api,selected.variant_id,prices);
        const campaign = window.RevivaAttribution?.values || {};
        // Cart attributes survive Shopify's checkout URL canonicalisation. Update only
        // campaign keys; preserve all unrelated cart attributes and line properties.
        if (Object.keys(campaign).length) {
          checkSnapshot(await api.get(),expected);
          const saved = await api.update({attributes:campaign});
          if (Object.entries(campaign).some(([k,v])=>saved.attributes?.[k] !== v)) {
            throw new Error('Campaign details could not be saved. Reload before continuing.');
          }
        }
        const current=await api.get(); checkSnapshot(current,expected);
        if (!packageCart(current.items,prices,bump)) throw new Error('Review the saved cart before checkout.');
        if (bump && current.items.some(item=>Number(item.variant_id)===Number(bump.id))) await checkExtra(api,bump);
        // Native Shopify remains authoritative for final shipping, taxes and payment.
        location.assign(attributed(form.dataset.checkoutUrl));
      });
    } catch(e) {fail(e.message);}
  });
  window.addEventListener('pageshow',event=>{if(event.persisted) location.replace(savedUrl()); else refresh();});
  refresh();
  // Sales offers navigate here (GET), never directly POST-add. A restored URL is a no-op.
  if (incoming && (selected || empty)) choose(incoming);
})();
